import useConfirmation from '../common/useConfirmation';
import LocalizedMessage from '../common/LocalizedMessage';
import React, { useEffect, useMemo, useState } from "react";
import PropTypes from "prop-types";
import { Modal } from "react-bootstrap";
import ClientLedgerForm from "../manage/ClientLedgerForm";
import ClientLedgerList from "../manage/ClientLedgerList";
import { CLIENT_LEDGER_ENTRY_TYPES, PAYMENT_METHODS } from "../manage/clientLedgerTypes";
import { normalizeClientLedgerEntry, validateClientLedgerEntry } from "../manage/clientLedgerValidation";
import { buildDefaultPaymentDate, isDateInMonth } from "./clientSettlementPayments";
import { useTranslation } from 'react-i18next';

const OUT_OF_MONTH_DATE_ERROR = "Data wplaty musi nalezec do wybranego miesiaca.";

const createEmptyPaymentEntry = (clientId, month) => ({
  clientId,
  type: CLIENT_LEDGER_ENTRY_TYPES.PAYMENT,
  date: buildDefaultPaymentDate(month),
  settlementMonth: month,
  amount: "",
  method: PAYMENT_METHODS.TRANSFER,
  note: "",
});

function validatePaymentEntry(entry, month) {
  const validationErrors = validateClientLedgerEntry(entry);

  if (!isDateInMonth(entry.date, month)) {
    validationErrors.date = OUT_OF_MONTH_DATE_ERROR;
  }

  return validationErrors;
}

function ClientPaymentsModal({ show, clientName, clientId, month, entries, onClose, onSave, onDelete }) {
  const { confirm, confirmation } = useConfirmation();
  const { t: tr } = useTranslation();
  const [entry, setEntry] = useState(createEmptyPaymentEntry(clientId, month));
  const [errors, setErrors] = useState({});
  const [editingEntryId, setEditingEntryId] = useState("");

  useEffect(() => {
    if (show) {
      setEntry(createEmptyPaymentEntry(clientId, month));
      setErrors({});
      setEditingEntryId("");
    }
  }, [clientId, month, show]);

  const renderedEntries = useMemo(() => entries
    .filter(currentEntry => currentEntry.type === CLIENT_LEDGER_ENTRY_TYPES.PAYMENT), [entries]);

  const handleChange = event => {
    const { name, value } = event.target;

    if (name === "settlementMonth") return;

    setEntry(currentEntry => ({ ...currentEntry, [name]: value }));
    if (name === "date") {
      setErrors(currentErrors => ({
        ...currentErrors,
        date: isDateInMonth(value, month) ? "" : OUT_OF_MONTH_DATE_ERROR,
      }));
    }
  };

  const handleEdit = selectedEntry => {
    setEntry({ ...selectedEntry, amount: String(selectedEntry.amount) });
    setErrors({});
    setEditingEntryId(selectedEntry.id);
  };

  const handleCancelEdit = () => {
    setEntry(createEmptyPaymentEntry(clientId, month));
    setErrors({});
    setEditingEntryId("");
  };

  const handleSubmit = event => {
    event.preventDefault();
    const scopedEntry = {
      ...entry,
      clientId,
      type: CLIENT_LEDGER_ENTRY_TYPES.PAYMENT,
      settlementMonth: month,
    };
    const validationErrors = validatePaymentEntry(scopedEntry, month);
    setErrors(validationErrors);
    if (Object.keys(validationErrors).length > 0) return;

    onSave(normalizeClientLedgerEntry(scopedEntry, Date.now().toString()), !editingEntryId)
      .then(() => handleCancelEdit())
      .catch(error => setErrors({ saveError: error.message || error }));
  };

  const handleDelete = async selectedEntry => {
    if (!(await confirm(tr("manage:extraDeleteThisPayment_105")))) return;

    onDelete(selectedEntry)
      .then(() => {
        if (editingEntryId === selectedEntry.id) {
          handleCancelEdit();
        }
      })
      .catch(error => setErrors({ saveError: error.message || error }));
  };

  return (
    <>{confirmation}<Modal show={show} onHide={onClose} centered size="lg">
      <Modal.Header closeButton closeLabel={tr("common:close")}>
        <Modal.Title>{tr('common:paymentsTitle', { name: clientName, month })}</Modal.Title>
      </Modal.Header>
      <Modal.Body>
        {errors.saveError && <div role="alert" className="alert alert-danger"><LocalizedMessage isError value={errors.saveError} /></div>}
        <ClientLedgerForm
          entry={entry}
          errors={errors}
          onChange={handleChange}
          onSubmit={handleSubmit}
          isEditing={!!editingEntryId}
          onCancel={handleCancelEdit}
          showTypeSelector={false}
          fixedTypeLabel="Wplata"
          settlementMonthReadOnly
        />
        <div className="mt-4">
          {renderedEntries.length === 0 ? (
            <div className="manage-text">{tr("billing:noPaymentsThisMonth_96")}</div>
          ) : (
            <ClientLedgerList entries={renderedEntries} onEdit={handleEdit} onDelete={handleDelete} />
          )}
        </div>
      </Modal.Body>
    </Modal></>
  );
}

ClientPaymentsModal.propTypes = {
  show: PropTypes.bool.isRequired,
  clientName: PropTypes.string.isRequired,
  clientId: PropTypes.string.isRequired,
  month: PropTypes.string.isRequired,
  entries: PropTypes.array.isRequired,
  onClose: PropTypes.func.isRequired,
  onSave: PropTypes.func.isRequired,
  onDelete: PropTypes.func.isRequired,
};

export default ClientPaymentsModal;
